import { useCallback, useEffect } from 'react';

import {
  useCurrentProjectId,
  useCurrentToolId,
  useUser,
} from '@adsk/alloy-provider';
import { useLocalState } from '@adsk/alloy-react-helpers';

import { RECENTS_DEFAULT_MAX_LENGTH } from '../consts';
import { RecentRecord } from '../types';

// useLocalState requires a non-falsy key at runtime. When userId, projectId, or
// toolId is not yet available we use this sentinel so the hook can always be
// called unconditionally.
const DISABLED_KEY = '__recents-disabled__';
// Shared for cross-component notification so that other components get notified
// and if using the same key update their list of recents
const RECENTS_UPDATED_EVENT = 'members-recents-updated';

export function buildStorageKey(
  userId: string,
  projectId: string,
  toolId: string,
  fieldId: string | undefined,
): string {
  return ['recents', projectId, userId, toolId, fieldId]
    .filter(Boolean)
    .join('_');
}

export function addRecord(
  existing: RecentRecord[],
  newRecord: RecentRecord,
): RecentRecord[] {
  const withoutDuplicates = existing.filter(
    (r) => !(r.id === newRecord.id && r.type === newRecord.type),
  );
  const withNew = [newRecord, ...withoutDuplicates];
  const countByType: Record<string, number> = {};

  return withNew.filter((r) => {
    countByType[r.type] = (countByType[r.type] ?? 0) + 1;
    return countByType[r.type] <= RECENTS_DEFAULT_MAX_LENGTH;
  });
}

function isRecentRecord(r: unknown): r is RecentRecord {
  return (
    typeof r === 'object' &&
    r !== null &&
    typeof (r as RecentRecord).id === 'string' &&
    typeof (r as RecentRecord).type === 'string'
  );
}

export const useRecentsPersistence = (fieldId?: string) => {
  const { user } = useUser();
  const projectId = useCurrentProjectId();
  const toolId = useCurrentToolId();

  const userId = user?.autodeskId;

  const storageKey =
    userId && projectId && toolId
      ? buildStorageKey(userId, projectId, toolId, fieldId)
      : undefined;

  const [storedRecents, setStoredRecents] = useLocalState<RecentRecord[]>(
    storageKey ?? DISABLED_KEY,
    [],
  );

  // Reads from localStorage and updates state. Used for the initial sync when
  // storageKey becomes available, and as the event handler for cross-instance sync.
  const refresh = useCallback(() => {
    if (!storageKey || typeof globalThis.window === 'undefined') return;
    try {
      const raw = globalThis.localStorage.getItem(storageKey);
      if (!raw) return;
      const parsed = JSON.parse(raw);
      if (Array.isArray(parsed))
        setStoredRecents(parsed.filter(isRecentRecord));
    } catch {
      // ignore malformed data
    }
  }, [storageKey, setStoredRecents]);

  // On mount and whenever storageKey changes (e.g. userId loads late), re-sync
  // from localStorage. Also listens for writes by other instances sharing the
  // same storage key so they stay in sync without threading onIsOpenChange.
  // useEffect only runs client-side so no SSR guard is needed.
  useEffect(() => {
    refresh();

    const handler = (e: Event) => {
      if ((e as CustomEvent<{ key: string }>).detail?.key === storageKey) {
        refresh();
      }
    };
    globalThis.addEventListener(RECENTS_UPDATED_EVENT, handler);
    return () => globalThis.removeEventListener(RECENTS_UPDATED_EVENT, handler);
  }, [refresh, storageKey]);

  // Always reads directly from localStorage so multiple instances sharing the
  // same key stay in sync regardless of each instance's local React state.
  const refreshRecents = useCallback((): RecentRecord[] => {
    if (!storageKey || typeof globalThis.window === 'undefined') return [];
    try {
      const raw = globalThis.localStorage.getItem(storageKey);
      if (!raw) return [];
      const parsed = JSON.parse(raw);
      if (!Array.isArray(parsed)) return [];
      return parsed.filter(isRecentRecord);
    } catch {
      return [];
    }
  }, [storageKey]);

  const addToRecents = useCallback(
    (item: RecentRecord) => {
      if (!storageKey) return;
      const updated = addRecord(refreshRecents(), {
        id: item.id,
        type: item.type,
      });
      // Write to localStorage eagerly so that concurrent calls within the same
      // React batch see this write via refreshRecents() before the flush.
      try {
        globalThis.localStorage.setItem(storageKey, JSON.stringify(updated));
        setStoredRecents(updated);
        globalThis.dispatchEvent(
          new CustomEvent(RECENTS_UPDATED_EVENT, {
            detail: { key: storageKey },
          }),
        );
      } catch {
        // storage unavailable / quota exceeded — skip persistence, recents stay in-memory only
      }
    },
    [storageKey, refreshRecents, setStoredRecents],
  );

  return {
    recents: storageKey ? (storedRecents ?? []) : [],
    addToRecents,
    refreshRecents,
  };
};
